import { useEffect, useRef, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../auth/msalConfig";

const WS_BASE = import.meta.env.VITE_WS_URL as string;
const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

export type WsReadyState = "CONNECTING" | "OPEN" | "CLOSED";

export interface UseWebSocketResult {
  lastMessage: unknown;
  readyState: WsReadyState;
}

export function useWebSocket(): UseWebSocketResult {
  const { instance, accounts } = useMsal();
  const [lastMessage, setLastMessage] = useState<unknown>(null);
  const [readyState, setReadyState] = useState<WsReadyState>("CLOSED");

  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffRef = useRef(1000);
  const unmountedRef = useRef(false);

  const clearPing = () => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  };

  const clearReconnect = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const connect = useCallback(async () => {
    if (unmountedRef.current) return;
    if (!accounts.length) return;

    let token: string;
    try {
      const result = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      token = result.accessToken;
    } catch {
      // Token refresh failed — retry after backoff
      scheduleReconnect();
      return;
    }

    setReadyState("CONNECTING");
    const url = `${WS_BASE}/ws/detections?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmountedRef.current) { ws.close(); return; }
      setReadyState("OPEN");
      backoffRef.current = 1000;

      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "pong" }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const data: unknown = JSON.parse(event.data as string);
        setLastMessage(data);
      } catch {
        // ignore unparseable frames
      }
    };

    ws.onerror = () => {
      // onclose will fire next and trigger reconnect
    };

    ws.onclose = () => {
      clearPing();
      if (!unmountedRef.current) {
        setReadyState("CLOSED");
        scheduleReconnect();
      }
    };
  }, [instance, accounts]);

  const scheduleReconnect = useCallback(() => {
    clearReconnect();
    const delay = backoffRef.current;
    backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
    reconnectTimerRef.current = setTimeout(() => {
      if (!unmountedRef.current) connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    unmountedRef.current = false;
    connect();

    return () => {
      unmountedRef.current = true;
      clearPing();
      clearReconnect();
      wsRef.current?.close();
    };
  }, [connect]);

  return { lastMessage, readyState };
}
